/**
 * DisaChat — shared chat UI used by both the floating widget and the full /disa page.
 *
 * Props:
 *  compact    (bool)  – widget mode (smaller, no sidebar)
 *  onClose    (fn)    – called when user clicks the × in compact mode
 *  onExpand   (fn)    – called when user clicks "Open full chat" in compact mode
 *  conversationId     – controlled conversation id (for full-page use)
 *  onConversationId   – called with the new conversation id on first reply
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Send, X, Maximize2, Check, RotateCcw, Flame, UserRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { tokenStore } from "@/lib/api";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import DisaAboutModal from "@/components/disa/DisaAboutModal";

const DISA_AVATAR = "/assets/images/disa-avatar.png";

const AUTH_STARTERS = [
  { id: "s1", text: "Help me find an activity for my child" },
  { id: "s2", text: "Explain my child's latest assessment" },
  { id: "s3", text: "What milestones should my child hit at this age?" },
  { id: "s4", text: "Suggest a fun game for my child" },
];

const GUEST_STARTERS = [
  { id: "g1", text: "What is Kiddo+?" },
  { id: "g2", text: "What makes Kiddo+ different?" },
  { id: "g3", text: "How does the child assessment work?" },
  { id: "g4", text: "What age groups does Kiddo+ support?" },
];

// Generate a stable local id for messages
let _msgSeq = 0;
const nextId = () => `m${++_msgSeq}`;

function TypingDots() {
  return (
    <div className="flex items-center gap-[3px] px-2 py-2" data-testid="disa-typing-indicator">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-1.5 h-1.5 rounded-full bg-teal-500 opacity-70 animate-bounce"
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </div>
  );
}

function MessageBubble({ msg }) {
  const isUser = msg.role === "user";
  // Historical DB messages (no _tick) default to 'delivered'; new messages use _tick
  const tickState = isUser ? (msg._tick || "delivered") : null;

  if (isUser) {
    return (
      <div className="flex justify-end mb-2" data-testid="disa-user-message">
        <div className="max-w-[78%]">
          <div className="bg-teal-600 text-white rounded-2xl rounded-br-md px-4 py-2.5 text-base leading-relaxed shadow-sm">
            {msg.content}
          </div>
          <div className="flex justify-end items-center gap-1 mt-0.5 pr-0.5">
            <span className="text-[10px] text-gray-400">
              {new Date(msg.created_at || Date.now()).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </span>
            {tickState === "delivered" ? (
              <span
                key="double"
                className="flex text-teal-500 animate-in fade-in duration-200"
                title="Delivered"
                data-testid="disa-delivered-tick"
              >
                <Check className="w-3 h-3 -mr-1.5" strokeWidth={3} />
                <Check className="w-3 h-3" strokeWidth={3} />
              </span>
            ) : (
              <span key="single">
                <Check className="w-3 h-3 text-gray-400" strokeWidth={3} title="Sent" data-testid="disa-sent-tick" />
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-end gap-2 mb-2" data-testid="disa-assistant-message">
      <img
        src={DISA_AVATAR}
        alt="DISA"
        className="w-8 h-8 rounded-full object-cover shrink-0 mb-0.5 shadow-sm border-2 border-white"
      />
      <div className="max-w-[78%]">
        <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 text-gray-800 dark:text-gray-100 rounded-2xl rounded-bl-md px-4 py-2.5 text-base leading-relaxed shadow-sm whitespace-pre-wrap">
          {msg._streaming && !msg.content ? <TypingDots /> : msg.content}
        </div>
        <span className="text-[10px] text-gray-400 pl-1 mt-0.5 block">
          {new Date(msg.created_at || Date.now()).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </span>
      </div>
    </div>
  );
}

export default function DisaChat({
  compact = false,
  onClose,
  onExpand,
  conversationId: controlledConvId,
  onConversationId,
  initialMessages = [],   // pre-loaded messages for instant display (widget session restore)
  onMessagesChange,       // (messages, convId) => void — called after each exchange for persistence
  streakDays = 0,         // activity streak days — shown as flame chip in compact header
}) {
  const { user } = useAuth();
  const [messages, setMessages] = useState(initialMessages);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [anonCount, setAnonCount] = useState(0);
  const [guestLocked, setGuestLocked] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [convId, setConvId] = useState(controlledConvId || null);
  const [childName, setChildName] = useState("");
  const [dailyLimitReached, setDailyLimitReached] = useState(false);
  const [messagesRemaining, setMessagesRemaining] = useState(null); // null = unlimited/unknown
  const [bridgeVisible, setBridgeVisible] = useState(false);
  const [bridgeSent, setBridgeSent] = useState(false);
  const [bridgeSending, setBridgeSending] = useState(false);
  const bridgeShownRef = useRef(false); // fire bridge card only once per session
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const inactivityTimer = useRef(null);
  const ownConvIdRef = useRef(null);

  // Fetch child's first name for personalized intro
  useEffect(() => {
    if (!user) return;
    const token = tokenStore.get();
    fetch(`${API}/children/me`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => r.json())
      .then((d) => {
        const raw = d?.name || "";
        const first = raw.trim().split(" ")[0];
        if (first && first.toLowerCase() !== "n/a") setChildName(first);
      })
      .catch(() => {});
  }, [user]); // eslint-disable-line

  // Check daily DISA usage for free-tier logged-in users on mount
  useEffect(() => {
    if (!user) return;
    const token = tokenStore.get();
    fetch(`${API}/disa/usage`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => r.json())
      .then((d) => {
        if (!d.is_unlimited) {
          const rem = d.messages_remaining ?? null;
          setMessagesRemaining(rem);
          if (rem !== null && rem <= 0) setDailyLimitReached(true);
        }
      })
      .catch(() => {});
  }, [user]); // eslint-disable-line

  // Sync controlled conversation id — triggered by user clicking a sidebar conversation.
  // SKIP clearing messages if we just set this id ourselves during streaming.
  useEffect(() => {
    // Parent set convId to null → "New Chat" was clicked, clear everything
    if (controlledConvId === null) {
      setMessages([]);
      setConvId(null);
      setStreaming(false);
      setGuestLocked(false);
      setAnonCount(0);
      setBridgeVisible(false);
      setBridgeSent(false);
      bridgeShownRef.current = false;
      return;
    }
    if (controlledConvId !== convId) {
      setConvId(controlledConvId);
      if (controlledConvId !== ownConvIdRef.current) {
        // Real navigation to a different conversation — clear current messages
        setMessages([]);
      }
      ownConvIdRef.current = null; // reset after consumed
    }
  }, [controlledConvId]); // eslint-disable-line

  // Load existing messages ONLY when a conversation is selected from the sidebar
  // (when controlledConvId changes from parent). Do NOT trigger on internal convId
  // changes during streaming — that would wipe the in-progress message.
  useEffect(() => {
    if (!controlledConvId || !user) return;
    const token = tokenStore.get();
    fetch(`${API}/disa/conversations/${controlledConvId}/messages`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => r.json())
      .then((msgs) => {
        if (Array.isArray(msgs) && msgs.length > 0) setMessages(msgs);
      })
      .catch(() => {});
  }, [controlledConvId, user]);

  // Auto-scroll to bottom
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streaming]);

  // Persist session — notify parent after each complete exchange (not during streaming)
  useEffect(() => {
    if (!onMessagesChange || streaming) return;
    onMessagesChange(messages, convId);
  }, [messages, convId, streaming]); // eslint-disable-line

  // Auto-close after 30 min inactivity (compact widget only)
  useEffect(() => {
    if (!compact || !onClose) return;
    clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      onClose();
    }, 30 * 60 * 1000);
    return () => clearTimeout(inactivityTimer.current);
  }, [messages, compact, onClose]);

  const sendMessage = useCallback(
    async (text) => {
      const msg = text?.trim() || input.trim();
      if (!msg || streaming || guestLocked || dailyLimitReached) return;

      // Guest hard limit — after 4 exchanges DISA says goodbye and input locks
      if (!user && anonCount >= 4) {
        setMessages((prev) => [
          ...prev,
          {
            id: nextId(),
            role: "assistant",
            content:
              "I've loved chatting with you! To get personalized therapy plans and save our conversation, please create an account or log in.",
            created_at: new Date().toISOString(),
          },
        ]);
        setGuestLocked(true);
        return;
      }

      setInput("");

      const userMsgId = nextId();
      const placeholderId = nextId();

      // ── Fire API immediately (parallel with all UI timers) ──────────────
      setStreaming(true);
      if (!user) setAnonCount((c) => c + 1);

      const token = tokenStore.get();
      const apiPromise = fetch(`${API}/disa/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          message: msg,
          conversation_id: convId,
          anonymous_message_count: anonCount,
        }),
      });

      // ── t=100ms: user message + single grey tick ─────────────────────────
      await new Promise((r) => setTimeout(r, 100));
      setMessages((prev) => [
        ...prev,
        {
          id: userMsgId,
          role: "user",
          content: msg,
          created_at: new Date().toISOString(),
          _local: true,
          _tick: "sent",
        },
      ]);

      // ── t=400ms: double blue tick ────────────────────────────────────────
      await new Promise((r) => setTimeout(r, 400)); // 100+300 = 400ms total
      setMessages((prev) =>
        prev.map((m) => (m.id === userMsgId ? { ...m, _tick: "delivered" } : m))
      );

      // ── t=800ms: typing dots appear ──────────────────────────────────────
      await new Promise((r) => setTimeout(r, 800)); // 400+400 = 800ms total
      setMessages((prev) => [
        ...prev,
        {
          id: placeholderId,
          role: "assistant",
          content: "",
          created_at: new Date().toISOString(),
          _streaming: true,
        },
      ]);

      // ── t=API response: word-by-word animation ──────────────────────────
      try {
        const resp = await apiPromise;

        // Daily limit exceeded (free logged-in user)
        if (resp.status === 429) {
          const errData = await resp.json().catch(() => ({}));
          if (errData.detail?.code === "daily_limit_reached") {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === placeholderId
                  ? {
                      ...m,
                      content:
                        "You've reached your daily chat limit with DISA. Upgrade to Kiddo+ Pro for unlimited support and personalized progress analysis!",
                      _streaming: false,
                    }
                  : m
              )
            );
            setDailyLimitReached(true);
            setMessagesRemaining(0);
            return;
          }
        }

        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();

        if (data.conversation_id) {
          ownConvIdRef.current = data.conversation_id;
          setConvId(data.conversation_id);
          onConversationId?.(data.conversation_id);
        }

        const words = (data.response || "").split(" ");
        let shown = "";
        const CHUNK = 4;
        for (let i = 0; i < words.length; i += CHUNK) {
          const chunk = words.slice(i, i + CHUNK).join(" ");
          shown += (shown ? " " : "") + chunk;
          const snap = shown;
          setMessages((prev) =>
            prev.map((m) => (m.id === placeholderId ? { ...m, content: snap } : m))
          );
          await new Promise((res) => setTimeout(res, 35));
        }

        setMessages((prev) =>
          prev.map((m) => (m.id === placeholderId ? { ...m, _streaming: false } : m))
        );

        // ── Bridge to Human — inject DISA message + show card once ──────
        if (data.bridge_triggered && !bridgeShownRef.current) {
          bridgeShownRef.current = true;
          setBridgeVisible(true);
          setMessages((prev) => [
            ...prev,
            {
              id: nextId(),
              role: "assistant",
              content:
                "I want to make sure you get the best advice. Would you like to send this query directly to our therapist for a personal review?",
              created_at: new Date().toISOString(),
            },
          ]);
        }

        // ── Inject warning / limit messages based on remaining count ─────
        if (!data.is_unlimited && data.messages_remaining != null) {
          const rem = data.messages_remaining;
          setMessagesRemaining(rem);
          if (rem === 0) {
            setMessages((prev) => [
              ...prev,
              {
                id: nextId(),
                role: "assistant",
                content:
                  "You've reached your daily chat limit with DISA. Upgrade to Kiddo+ Pro for unlimited support and personalized progress analysis!",
                created_at: new Date().toISOString(),
              },
            ]);
            setDailyLimitReached(true);
          } else if (rem === 2) {
            setMessages((prev) => [
              ...prev,
              {
                id: nextId(),
                role: "assistant",
                content:
                  "Just a heads-up — you have 2 messages left with DISA today. Upgrade to Pro for unlimited chat!",
                created_at: new Date().toISOString(),
              },
            ]);
          }
        }
      } catch (err) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === placeholderId
              ? { ...m, content: "Sorry, I couldn't connect right now. Please try again.", _streaming: false }
              : m
          )
        );
      } finally {
        setStreaming(false);
        inputRef.current?.focus();
      }
    },
    [input, streaming, guestLocked, dailyLimitReached, convId, user, anonCount, onConversationId, bridgeShownRef]
  );

  const sendToTherapist = useCallback(async () => {
    if (!convId || bridgeSending || bridgeSent) return;
    setBridgeSending(true);
    const token = tokenStore.get();
    try {
      const resp = await fetch(`${API}/disa/send-to-therapist`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ conversation_id: convId }),
      });
      if (resp.ok) setBridgeSent(true);
    } catch {
      // silent — user sees the button stay available
    } finally {
      setBridgeSending(false);
    }
  }, [convId, bridgeSending, bridgeSent]);

  const handleKey = (e) => {    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setConvId(null);
    setAnonCount(0);
    setStreaming(false);
    setGuestLocked(false);
    setBridgeVisible(false);
    setBridgeSent(false);
    bridgeShownRef.current = false;
    onMessagesChange?.([], null); // immediately clear persisted session
  };

  const isEmpty = messages.length === 0;
  const showLoginCta = guestLocked;

  return (
    <div
      className="flex flex-col h-full"
      data-testid="disa-chat-container"
      onClick={() => { clearTimeout(inactivityTimer.current); }}
    >
      {/* ── Header — only shown in compact/widget mode ── */}
      {compact && (
      <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-teal-600 to-teal-700 text-white shrink-0 rounded-t-2xl">
        <button
          onClick={() => setShowAbout(true)}
          className="w-9 h-9 rounded-full overflow-hidden border-2 border-white/40 shadow-sm shrink-0 active:opacity-75 transition-opacity"
          title="About DISA"
          data-testid="disa-widget-avatar-btn"
        >
          <img
            src={DISA_AVATAR}
            alt="DISA"
            className="w-full h-full object-cover"
          />
        </button>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm leading-tight">DISA</p>
          <div className="flex items-center gap-1.5 min-w-0">
            <p className="text-[10px] text-teal-100 truncate">AI Therapy Assistant · Kiddo+</p>
            {streakDays >= 2 && (
              <span
                data-testid="disa-streak-chip"
                title={`${streakDays}-day activity streak`}
                className="flex items-center gap-0.5 bg-orange-400/25 border border-orange-300/40 rounded-full px-1.5 py-px text-[9px] font-bold text-orange-100 shrink-0"
              >
                <Flame className="w-2.5 h-2.5 text-orange-300" />
                {streakDays}d
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {messages.length > 0 && (
            <button
              onClick={clearChat}
              title="New chat"
              className="w-7 h-7 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors"
              data-testid="disa-new-chat-btn"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          )}
          {onExpand && (
            <button
              onClick={onExpand}
              title="Open full chat"
              className="w-7 h-7 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors"
              data-testid="disa-expand-btn"
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
          )}
          {onClose && (
            <button
              onClick={onClose}
              title="Close"
              className="w-7 h-7 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors"
              data-testid="disa-close-btn"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      )}

      {/* About DISA modal — compact widget + full page */}
      {showAbout && <DisaAboutModal onClose={() => setShowAbout(false)} />}

      {/* ── Messages ── */}
      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5 bg-gray-50 dark:bg-gray-900" data-testid="disa-messages-area">
        {isEmpty && (
          <div className="flex flex-col px-2 pt-3 pb-2" data-testid="disa-welcome-state">
            {/* DISA intro message bubble */}
            <div className="flex items-end gap-2 mb-3">
              <img
                src={DISA_AVATAR}
                alt="DISA"
                className="w-8 h-8 rounded-full object-cover shrink-0 mb-0.5 shadow-sm border-2 border-white"
              />
              <div className="max-w-[85%]">
                <div
                  className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 text-gray-800 dark:text-gray-100 rounded-2xl rounded-bl-md px-4 py-3 text-base leading-relaxed shadow-sm"
                  data-testid="disa-intro-message"
                >
                  {user && childName
                    ? `Hi! I'm DISA. How can I help with ${childName}'s development today?`
                    : user
                    ? "Hi! I'm DISA. How can I help with your child's development today?"
                    : "Hi! I'm DISA. Ask me anything about Kiddo+ or child development!"
                  }
                </div>
              </div>
            </div>

            {/* Conversation starters — hidden when daily limit reached */}
            {!dailyLimitReached && (
            <div className="pl-10 space-y-1.5" data-testid="disa-starters">
              {(user ? AUTH_STARTERS : GUEST_STARTERS).map((s) => (
                <button
                  key={s.id}
                  onClick={() => sendMessage(s.text)}
                  data-testid={`disa-starter-${s.id}`}
                  className="w-full text-left text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl px-3 py-2 text-gray-600 dark:text-gray-300 hover:border-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-all leading-snug"
                >
                  {s.text}
                </button>
              ))}
            </div>
            )}
          </div>
        )}

        {messages.map((msg, i) => (
          <MessageBubble key={msg.id || i} msg={msg} />
        ))}

        {/* Login CTA — shown after guest limit is reached */}
        {showLoginCta && (
          <div className="mx-1 my-2 p-3 bg-teal-50 dark:bg-teal-900/30 rounded-xl border border-teal-200 dark:border-teal-700 text-center" data-testid="disa-login-nudge">
            <p className="text-xs text-teal-800 dark:text-teal-200 font-medium mb-2">
              Create a free account to continue chatting and get personalised answers for your child.
            </p>
            <div className="flex gap-2 justify-center">
              <Link
                to="/signup"
                className="inline-block bg-teal-600 text-white text-xs font-bold rounded-full px-4 py-1.5 hover:bg-teal-700 transition-colors"
                data-testid="disa-signup-cta"
              >
                Create account
              </Link>
              <Link
                to="/login"
                className="inline-block bg-white border border-teal-300 text-teal-700 text-xs font-bold rounded-full px-4 py-1.5 hover:bg-teal-50 transition-colors"
                data-testid="disa-login-cta"
              >
                Log in
              </Link>
            </div>
          </div>
        )}

        {/* Daily limit CTA — shown when free user hits their 10-message daily cap */}
        {dailyLimitReached && !showLoginCta && (
          <div className="mx-1 my-2 p-3 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/30 dark:to-amber-900/30 rounded-xl border border-orange-200 dark:border-orange-700 text-center" data-testid="disa-daily-limit-cta">
            <p className="text-xs font-semibold text-orange-800 dark:text-orange-200 mb-0.5">Daily limit reached</p>
            <p className="text-[11px] text-orange-600 dark:text-orange-300 mb-2">Resets at midnight · Upgrade for unlimited access</p>
            <Link
              to="/subscription"
              className="inline-block bg-gradient-to-r from-orange-500 to-amber-500 hover:opacity-90 text-white text-xs font-bold rounded-full px-5 py-1.5 transition-opacity shadow-sm"
              data-testid="disa-upgrade-cta"
            >
              Upgrade to Pro
            </Link>
          </div>
        )}

        {/* Bridge to Human card — shown when trigger fires */}
        {bridgeVisible && (
          <div
            className="mx-1 my-2 rounded-2xl border overflow-hidden shadow-sm"
            style={{ background: "linear-gradient(135deg, #EAF7F4 0%, #F0F4FF 100%)", borderColor: "#B2DDD6" }}
            data-testid="disa-bridge-card"
          >
            <div className="flex items-start gap-3 px-4 pt-4 pb-3">
              <div className="w-9 h-9 rounded-full bg-teal-600 flex items-center justify-center shrink-0 mt-0.5">
                <UserRound className="w-4.5 h-4.5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-teal-900 leading-snug">Connect with a real therapist</p>
                <p className="text-xs text-teal-700 mt-0.5 leading-relaxed">
                  {bridgeSent
                    ? "Your conversation has been sent. A Kiddo+ therapist will review it and reply to your email within 24–48 hours."
                    : "One click sends your full conversation to our team. A Kiddo+ therapist will read it and reply personally to your email."}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 px-4 pb-3">
              {!bridgeSent ? (
                <>
                  <button
                    onClick={sendToTherapist}
                    disabled={bridgeSending}
                    data-testid="disa-send-to-therapist-btn"
                    className="flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white text-xs font-bold rounded-full px-4 py-1.5 transition-all active:scale-95 shadow-sm"
                  >
                    <UserRound className="w-3.5 h-3.5" />
                    {bridgeSending ? "Sending…" : "Send to Therapist"}
                  </button>
                  <button
                    onClick={() => setBridgeVisible(false)}
                    data-testid="disa-bridge-dismiss-btn"
                    className="text-xs text-teal-600 hover:text-teal-800 font-medium px-2 py-1.5 rounded-full hover:bg-teal-100 transition-colors"
                  >
                    No thanks
                  </button>
                </>
              ) : (
                <div className="flex items-center gap-1.5 text-teal-700 text-xs font-semibold" data-testid="disa-bridge-sent-confirm">
                  <Check className="w-3.5 h-3.5" />
                  Sent — a therapist will be in touch soon!
                </div>
              )}
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* ── Input ── */}
      <div className="px-3 py-2.5 bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700 shrink-0 rounded-b-2xl">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder={dailyLimitReached ? "Daily limit reached — upgrade to continue…" : guestLocked ? "Log in to continue chatting…" : "Ask DISA anything…"}
            disabled={streaming || guestLocked || dailyLimitReached}
            data-testid="disa-input"
            className="flex-1 resize-none text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent placeholder:text-gray-400 text-gray-800 dark:text-gray-100 max-h-24 leading-relaxed transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ minHeight: "40px" }}
            onInput={(e) => {
              e.target.style.height = "auto";
              e.target.style.height = Math.min(e.target.scrollHeight, 96) + "px";
            }}
          />
          <button
            onClick={() => sendMessage()}
            disabled={!input.trim() || streaming || guestLocked || dailyLimitReached}
            data-testid="disa-send-btn"
            className="w-10 h-10 rounded-xl bg-teal-600 hover:bg-teal-700 disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center justify-center shrink-0 transition-all active:scale-95 shadow-sm"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[8px] text-gray-400/70 text-center mt-1 leading-tight">DISA is an AI assistant — always consult a therapist for medical decisions.</p>
      </div>
    </div>
  );
}
